<template>
  <div v-loading="loading" class="recordCard">
    <div class="recordCard__card" :class="{ opened: opened }">
      <img
        class="recordCard__preview"
        :class="{ update: hasUpdates }"
        :src="imageFromArpej"
      />
      <div class="recordCard__decorator">
        <i class="el-icon-folder-opened el-icon--left"></i>
        <div>
          <h5>
            <router-link :to="'/dossiers/' + record.id + '/reservation'">
              {{ $t('records.card.recordNmb') }}{{ record.number }}
            </router-link>
          </h5>
          <div v-if="opened">
            <md-icon v-if="record.state === 'finished'" name="stars" />
            <p>
              {{ $t('records.card.createdAt') }}
              {{ $moment(record.created_at).format('DD/MM/YYYY') }}
            </p>
            <p>
              {{ $t('records.card.wantedPeriod') }}
              <span>
                {{
                  record.desired_started_at
                    ? $moment(record.desired_started_at).format('DD/MM/YYYY')
                    : '?'
                }}
                {{ $t('records.card.to') }}
                {{
                  record.desired_finished_at
                    ? $moment(record.desired_finished_at).format('DD/MM/YYYY')
                    : '?'
                }}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div class="recordCard__decorator">
        <i class="el-icon-s-home el-icon--left"></i>
        <div>
          <h5 v-if="record && record.residence">
            {{ record.residence.title }}
          </h5>
          <div v-if="opened && record">
            <p v-if="record.residence">
              {{
                record.residence.address +
                  ', ' +
                  record.residence.zip_code +
                  ' ' +
                  record.residence.city
              }}
            </p>
            <h5 v-if="record.offer">
              {{ record.offer.unit_type.commercial_title }}
            </h5>
            <p v-if="record.offer">
              {{ $t('records.grid.typeOf') }} {{ record.offer.unit_type.kind }}
            </p>
            <p v-if="record.offer">
              <surface-display
                with-surface
                :with-bold="true"
                :offer="record.offer"
              />
            </p>
            <p v-if="record.offer">
              <rent-display
                with-ttc
                :with-bold="true"
                with-per-month
                :offer="record.offer"
              />
            </p>
            <h5 v-if="residencesServices && residencesServices.length">
              {{ $t('records.card.optinalServices') }}
            </h5>
            <ul
              v-if="residencesServices && residencesServices.length"
              class="recordCard__optional_services"
            >
              <li v-for="service in residencesServices" :key="service">
                {{ `${service.title} (${service.price})` }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="recordCard__progress">
        <div v-if="record && record.progress">
          <p>{{ $t('residences.folderCard.state') }}</p>
          <div>
            <el-progress
              class="recordCard__loadingBar"
              :show-text="false"
              :percentage="record.progress"
            ></el-progress>
            <span>{{ record.state_label }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="recordCard__actions">
      <div>
        <el-link
          :class="{ disabled: !isSigned }"
          :href="record.signed_pdf_file_url"
          target="_blank"
        >
          <span>
            <md-icon name="visibility" />
            <p>{{ $t('records.grid.seeMyContract') }}</p>
          </span>
        </el-link>
        <el-button
          :class="{
            disabled: record.state === 'refused_tenant_withdrawal' || isSigned
          }"
          @click="openDialog"
        >
          <md-icon name="archive" />
          <p>{{ $t('records.grid.archiveRecord') }}</p>
        </el-button>
      </div>
      <div>
        <el-button v-if="hasUpdates" type="primary" @click="update">{{
          $t('records.grid.update')
        }}</el-button>
        <el-button v-else type="primary" @click="setCurrent()">
          <md-icon name="create" />
          <p>{{ $t('records.grid.modifyMyRecord') }}</p>
        </el-button>
        <div class="recordCard__button" @click="opened = !opened">
          <el-tooltip
            :content="
              $t('residences.accomodation.see') +
                ' ' +
                (opened
                  ? $t('residences.accomodation.less')
                  : $t('residences.accomodation.more'))
            "
            placement="bottom"
            effect="light"
          >
            <i v-if="!opened" class="el-icon-arrow-down" />
            <i v-if="opened" class="el-icon-arrow-up" />
          </el-tooltip>
        </div>
      </div>
    </div>
    <el-dialog
      class="recordCard__dialog"
      :title="$t('records.grid.dialogTitle')"
      :before-close="handleClose"
      :visible.sync="dialogVisible"
      width="50%"
    >
      <p>
        {{ $t('records.grid.doCancel') }}&nbsp;<span class="primaryColor">{{
          record.number
        }}</span
        >&nbsp;?
      </p>
      <div class="recordCard__dialog-checkbox">
        <el-radio v-model="confirmArchive" :label="true">{{
          $t('records.grid.iCancel')
        }}</el-radio>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          :loading="dialogLoading"
          :disabled="!confirmArchive"
          @click="handleConfirm"
          >{{ $t('records.grid.confirm') }}</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import SurfaceDisplay from '@/components/ui/SurfaceDisplay'
import RentDisplay from '@/components/ui/RentDisplay'

export default {
  name: 'RecordCard',
  components: {
    SurfaceDisplay,
    RentDisplay
  },
  props: {
    record: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    loading: false,
    dialogLoading: false,
    dialogVisible: false,
    confirmArchive: false,
    opened: false
  }),
  computed: {
    hasUpdates() {
      return [
        'processing_missing_documents',
        'processing_missing_guarantors',
        'granted_waiting_price_acceptation'
      ].includes(this.record.state)
    },
    isSigned() {
      return ['granted_digitaly_signed', 'finished'].includes(this.record.state)
    },
    residencesServices() {
      return this.record && this.record.residence_services
        ? this.record.residence_services.filter(service => service.subscribed)
        : []
    },
    imageFromArpej() {
      return this.record &&
        this.record.residence &&
        this.record.residence.pictures &&
        this.record.residence.pictures.length > 0
        ? this.record.residence.pictures[0].url
        : 'https://www.arpej.fr/wp-content/uploads/2015/06/DSC01264.jpg'
    }
  },
  methods: {
    async setCurrent() {
      try {
        this.loading = true
        await this.$store.dispatch('records/getRecord', { id: this.record.id })
        this.$router.push({
          path: '/dossiers/' + this.record.id + '/reservation'
        })
        this.loading = false
      } catch (error) {
        this.loading = false
        this.$notify({
          type: 'error',
          title: this.$t('base.errors.notificationTitle'),
          message: this.$t('base.errors.defaultMessage')
        })
      }
    },
    openDialog() {
      this.dialogVisible = true
    },
    async handleConfirm() {
      if (this.confirmArchive) {
        this.dialogLoading = true
        try {
          await this.$store.dispatch('records/updateRecord', {
            id: this.record.id,
            record: {
              state: 'refused_tenant_withdrawal'
            }
          })

          this.$notify({
            type: 'success',
            title: this.$t('base.success.notificationTitle'),
            message: this.$t('records.card.dialogSuccess')
          })
        } catch (error) {
          console.warn(error)
        }
        this.dialogLoading = false
      }

      this.dialogVisible = false
    },
    update() {
      this.$emit('dialog', this.record.id)
    },
    handleClose() {
      this.confirmArchive = false
      this.dialogVisible = false
      this.dialogLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
$c: '.recordCard';

#{$c} {
  position: relative;
  font-size: 14px;
  width: calc(100% - 36px);
  margin: 12px;
  padding: 6px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(175, 175, 175, 0.5);

  @include respond-above(lg) {
    border-radius: 23px;
    padding: 0;
    width: calc(100% - 24px);
  }

  h5 {
    font-size: 14px;
    font-weight: 600;
    line-height: 26px;
    color: #303133;
    padding: 5px 30px;
    a {
      color: inherit;
    }

    @include respond-above(lg) {
      padding: 0;
    }
  }

  &__card {
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    align-items: center;
    margin-bottom: 15px;

    @include respond-above(lg) {
      align-items: start;
      text-align: center;
      height: 85px;
      display: grid;
      grid-template-columns: 155px repeat(2, 1fr) minmax(200px, 350px);
      &.opened {
        height: 300px;
      }
    }

    & > div {
      margin: 0 10px;

      @include respond-above(lg) {
        margin: 0 1%;
      }

      p {
        line-height: 1.75;
        color: #303133;
        margin: 0;
        @include respond-above(lg) {
          max-width: 100%;
        }
      }
    }
  }

  &__decorator {
    display: flex;
    align-items: center;
    display: block;
    border-top: 2px solid #f4b452;
    margin: 25px 2% 0 2% !important;
    max-width: 400px;
    min-width: 250px;
    text-align: center;

    @include respond-above(xl) {
      margin: 25px 5% 0 5% !important;
    }

    i {
      background-color: #ffffff;
      font-size: 30px;
      margin-top: -15px;
      margin-left: auto;
      margin-right: auto;
      padding: 0 20px;
    }
  }

  &__preview {
    height: 160px;
    width: 155px;
    object-fit: cover;
    border-radius: 23px 0 0 23px;

    &.update {
      border-radius: 23px 0 0 0;
    }

    @include respond-below(lg) {
      display: none;
    }
  }
  .opened &__preview {
    height: 395px;
  }

  &__progress {
    min-width: 150px;
    display: flex;
    align-items: center;
    flex-direction: column;
    margin: 15px 2% 0 2% !important;

    @include respond-between(xs, lg) {
      & > div {
        margin-left: 20px;
      }
    }

    @include respond-above(lg) {
      display: block;
    }

    & p {
      font-size: 12px;
      font-weight: bold;
      line-height: 30px;
      color: #303133;
      text-transform: uppercase;
      margin: 0;
      text-align: center;
    }
  }

  &__loadingBar {
    margin-bottom: 5px;
  }

  &__button {
    font-size: 20px;
    cursor: pointer;
    margin: 0 10px;
    text-align: right;
    display: inline-block;
    vertical-align: middle;
    position: absolute;
    right: 40px;
    top: 0;

    @include respond-above(lg) {
      font-size: 50px;
      margin: 0 20px;
      position: relative;
      right: 0px;
      top: -15px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: center;

    @include respond-above(lg) {
      flex-direction: row;
      justify-content: space-between;
      margin-left: 200px;
      margin-bottom: 20px;
      margin-right: 30px;

      a + .el-button {
        margin-left: 10px;
      }
    }

    & > div {
      display: flex;
      flex-direction: column;
      width: 200px;
      margin-bottom: 20px;
      @include respond-above(lg) {
        flex-direction: row;
        margin-bottom: 0;
      }
    }

    a {
      position: relative;
      display: flex;
      justify-content: center;
      white-space: nowrap;
      cursor: pointer;
      background: #ffffff;
      border: 1px solid #dcdfe6;
      padding: 12px 20px 9px 20px;
      font-size: 14px;
      color: #303133;
      border-radius: 3px;
      margin-bottom: 10px;
      @include respond-above(lg) {
        margin-bottom: 0;
      }
    }
    a:focus,
    a:hover {
      color: #f4b452;
      border-color: #fce9cb;
      background-color: #fef8ee;
      &:after {
        border: none;
      }
    }

    p {
      margin: 0;
      padding-left: 30px;
    }
    i {
      position: absolute;
      top: 8px;
      left: 20px;
    }

    .el-button {
      position: relative;
      display: flex;
      justify-content: center;
    }
  }

  &__updates {
    border-top: solid 1px #e4e7ed;
    padding: 10px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: space-between;

    p {
      color: #909399;
      padding-right: 5px;
    }
  }

  &__dialog {
    &-checkbox {
      margin-top: 24px;
    }
  }

  &__optional_services {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    padding-left: 20px;
  }
}
</style>
